import { diffLines, diffWordsWithSpace, type Change } from 'diff';

/** One run of a diff: text both sides share, or only one side has. */
export interface DiffPart {
  value: string;
  added: boolean;
  removed: boolean;
}

function part(change: Change): DiffPart {
  return {
    value: change.value,
    added: change.added === true,
    removed: change.removed === true,
  };
}

/** Spacing never counts as a change: removed spacing goes, added spacing is shared. */
function ignoreSpacing(changes: readonly Change[]): DiffPart[] {
  return changes.flatMap((change) => {
    if (!(change.added || change.removed) || change.value.trim())
      return [part(change)];
    return change.removed
      ? []
      : [{ value: change.value, added: false, removed: false }];
  });
}

/**
 * A readable diff of two texts: line by line first, so an unchanged line is
 * never marked, then word by word inside each pair of changed lines. Words and
 * punctuation are separate tokens and spacing never counts as a change.
 */
export function textDiff(before: string, after: string): DiffPart[] {
  const lines: Change[] = diffLines(before, after, { ignoreWhitespace: true });
  const parts: DiffPart[] = [];
  for (let index = 0; index < lines.length; index++) {
    const change = lines[index];
    const next = lines[index + 1];
    if (!change) continue;
    if (change.removed && next?.added) {
      // A changed line: mark only the words that differ within it.
      parts.push(
        ...ignoreSpacing(diffWordsWithSpace(change.value, next.value)),
      );
      index++;
    } else parts.push(part(change));
  }
  return parts;
}

/**
 * One side of a diff between two texts: the parts `side` lacks are left out,
 * the parts only it has are marked (deleted before, inserted after).
 */
export function WordDiff({
  before,
  after,
  side,
}: {
  before: string;
  after: string;
  side: 'before' | 'after';
}) {
  if (before === after) return <>{side === 'before' ? before : after}</>;
  return textDiff(before, after).map((change, index) => {
    if (side === 'before' && change.added) return null;
    if (side === 'after' && change.removed) return null;
    if (change.removed)
      return (
        <del key={index} className="bg-destructive/20 decoration-destructive">
          {change.value}
        </del>
      );
    if (change.added)
      return (
        <ins
          key={index}
          className="bg-emerald-500/20 underline decoration-emerald-600"
        >
          {change.value}
        </ins>
      );
    return <span key={index}>{change.value}</span>;
  });
}
